/**
 * Patients (`app/services/patient_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';
import { EmptyObject } from '@brainfocus/web/utils/types';
import { assertPojo } from '@brainfocus/web/api/response';
import type { Patient } from '@brainfocus/web/api/types';

export async function deletePatient(id: number): Promise<EmptyObject> {
    await client.request('POST', `/manage/patient/${id}/info/delete`, {});
    return {};
}

/**
 * Get all patient's in current user's institution
 */
export async function getPatients(): Promise<Patient[]> {
    const response = await client.request('POST', '/api/get-patients', {});

    assertPojo<{ patients: Patient[] }>(response);
    return response.patients;
}

/**
 * Get detail of patient belonging to user's insitution
 */
export async function getPatient(patientId: number): Promise<Patient> {
    const response = await client.request('POST', '/api/get-patient', { patientId });

    assertPojo<Patient>(response);
    return response;
}

/**
 * Upsert patient for current user's institution
 */
export async function setPatient(patient: PatientUpsert) {
    const r = await client.request('POST', '/api/set-patient', patient);
    assertPojo<Patient>(r);
    return r;
}

/**
 * Patient with optional ID serving as input for the patient upsert operation
 */
export type PatientUpsert = Omit<Patient, 'id' | 'seizureGroups' | 'mris'> & Partial<Pick<Patient, 'id'>>;
